import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

interface RequireAdminProps {
  children: React.ReactNode;
}

export default function RequireAdmin({ children }: RequireAdminProps) {
  const { isLoggedIn, isAdmin, isLoading, user, hasSiteAccess } = useAuth();
  const location = useLocation();

  // 加载中显示空白
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin text-xl" />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  // 未登录跳转到后台登录页
  if (!isLoggedIn) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // 根据用户角色决定可访问的路径
  const userRole = user?.role;
  const isTenantPath = location.pathname.startsWith('/tenant/');
  const isPlatformPath = location.pathname.startsWith('/platform/');
  const isAdminPath = location.pathname.startsWith('/admin/') && location.pathname !== '/admin/my-sites';

  // 平台管理员 (role === 'admin')
  if (userRole === 'admin') {
    // 平台管理员只能访问 /platform/* 和 /admin/my-sites
    if (isTenantPath) {
      return <Navigate to="/platform" replace />;
    }
    return <>{children}</>;
  }

  // 代理商 (role === 'tenant')
  if (userRole === 'tenant') {
    // 代理商只能访问 /tenant/* 和 /admin/my-sites
    if (isPlatformPath || isAdminPath) {
      return <Navigate to="/tenant" replace />;
    }
    return <>{children}</>;
  }

  // 其他角色（有站点访问权限的用户）可以访问 /admin/* 路径
  // 权限检查在后端 API 层进行
  if (hasSiteAccess || isAdmin || userRole === 'site_admin' || userRole === 'user') {
    // 禁止访问 /tenant/* 和 /platform/*
    if (isTenantPath || isPlatformPath) {
      return <Navigate to="/admin" replace />;
    }
    return <>{children}</>;
  }

  // 无站点访问权限，跳转到首页
  return <Navigate to="/" replace />;
}
